import api from './api';

class SourcingRfpService {
  static REQUEST_TIMEOUT_MS = 30000;

  static MAX_RETRIES = 3;

  static INITIAL_BACKOFF_MS = 500;

  static buildFullUrl(path) {
    const base = String(api?.defaults?.baseURL || '');
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    return `${base.replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`;
  }

  static sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  static shouldRetry(error) {
    if (!error) return false;
    if (error.code === 'ECONNABORTED') return true;
    if (!error.response) return true;
    const status = Number(error.response.status || 0);
    return status >= 500 || status === 429;
  }

  static logRequestFailure({ method, url, attempt, error, elapsedMs }) {
    console.error('[SourcingRfpService] request failed', {
      method,
      url,
      attempt,
      elapsedMs,
      code: error?.code,
      message: error?.message,
      status: error?.response?.status,
      response: error?.response?.data,
    });
  }

  static logRequestSuccess({ method, url, attempt, elapsedMs, status }) {
    console.log('[SourcingRfpService] request success', {
      method,
      url,
      attempt,
      elapsedMs,
      status,
    });
  }

  static async requestWithRetry(method, path, config = {}) {
    const methodUpper = String(method || 'GET').toUpperCase();
    const fullUrl = this.buildFullUrl(path);
    let lastError;

    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt += 1) {
      const startedAt = Date.now();
      try {
        const response = await api.request({
          method: methodUpper,
          url: path,
          timeout: config.timeout || this.REQUEST_TIMEOUT_MS,
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 304,
          ...config,
        });
        this.logRequestSuccess({
          method: methodUpper,
          url: fullUrl,
          attempt,
          elapsedMs: Date.now() - startedAt,
          status: response?.status,
        });
        return response;
      } catch (error) {
        lastError = error;
        this.logRequestFailure({
          method: methodUpper,
          url: fullUrl,
          attempt,
          error,
          elapsedMs: Date.now() - startedAt,
        });
        if (attempt >= this.MAX_RETRIES || !this.shouldRetry(error)) {
          break;
        }
        const backoff = this.INITIAL_BACKOFF_MS * 2 ** (attempt - 1);
        await this.sleep(backoff);
      }
    }

    throw lastError;
  }

  /*
  =============================
  RFP CRUD
  =============================
  */
  
  // Get all RFPs (with pagination + filters)
  static async getRfps(params = {}) {
    try {
      const response = await this.requestWithRetry('GET', '/sourcing/rfps', {
        params: { ...params, _ts: Date.now() },
      });
      return response.data || { success: true, data: [], items: [] };
    } catch (error) {
      console.error('Error fetching RFPs:', error);
      throw this.handleError(error);
    }
  }

  // Get single RFP
  static async getRfpById(id) {
    try {
      const response = await api.get(`/sourcing/rfps/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching RFP:', error);
      throw this.handleError(error);
    }
  }

  // Create RFP
  static async createRfp(data) {
    try {
      const response = await api.post('/sourcing/rfps', data);
      return response.data;
    } catch (error) {
      console.error('Error creating RFP:', error);
      throw this.handleError(error);
    }
  }

  // Update RFP
  static async updateRfp(id, data) {
    try {
      const response = await api.put(`/sourcing/rfps/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating RFP:', error);
      throw this.handleError(error);
    }
  }
  
  // Publish RFP
  static async publishRfp(id) {
    try {
      const response = await api.post(`/sourcing/rfps/${id}/publish`);
      return response.data;
    } catch (error) {
      console.error('Error publishing RFP:', error);
      throw this.handleError(error);
    }
  }
   
  // frontend/services/SourcingRfpService.js

static async deleteRfp(id) {
  try {
    const response = await api.delete(`/sourcing/rfps/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting RFP:', error);
    throw this.handleError(error);
  }
}
  /*
  =============================
  Vendor Responses
  =============================
  */

  static async getResponses(rfpId) {
    try {
      const endpoint = rfpId
        ? `/sourcing/rfps/${rfpId}/responses`
        : '/sourcing/rfps/responses';
      const response = await this.requestWithRetry('GET', endpoint, {
        params: { _ts: Date.now() },
      });
      return response.data || { success: true, data: [] };
    } catch (error) {
      console.error('Error fetching responses:', error);
      throw this.handleError(error);
    }
  }

  static async submitResponse(rfpId, data) {
    try {
      const response = await api.post(
        `/sourcing/rfps/${rfpId}/responses`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting response:', error);
      throw this.handleError(error);
    }
  }

  /*
  =============================
  Evaluation
  =============================
  */

  static async startEvaluation(id) {
    try {
      // This matches the route you defined in your backend controller
      const response = await api.post(`/sourcing/rfps/${id}/start-evaluation`);
      return response.data;
    } catch (error) {
      console.error('Error starting evaluation:', error);
      throw this.handleError(error);
    }
  }

  static async submitEvaluation(rfpId, data) {
    try {
      const response = await api.post(
        `/sourcing/rfps/${rfpId}/evaluation`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      throw this.handleError(error);
    }
  }

  static async getEvaluations(rfpId) {
    try {
      const response = await api.get(
        `/sourcing/rfps/${rfpId}/evaluation`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      throw this.handleError(error);
    }
  }

  /*
  =============================
  Award
  =============================
  */

  static async recommendAward(rfpId, data) {
    try {
      const response = await api.post(
        `/sourcing/rfps/${rfpId}/award`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error recommending award:', error);
      throw this.handleError(error);
    }
  }

  static async approveAward(awardId, data) {
    try {
      const response = await api.put(
        `/sourcing/awards/${awardId}/approve`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error approving award:', error);
      throw this.handleError(error);
    }
  }

  static async createContractFromAward(awardId) {
    try {
      const response = await api.post(
        `/sourcing/awards/${awardId}/create-contract`
      );
      return response.data;
    } catch (error) {
      console.error('Error creating contract from award:', error);
      throw this.handleError(error);
    }
  }

  /*
  =============================
  ERROR HANDLER
  (Same pattern as subcontracting)
  =============================
  */

  static handleError(error) {
    if (error.response) {
      const errorData = error.response.data;

      const errorMessage =
        errorData?.message ||
        errorData?.error ||
        (errorData?.errors?.length
          ? errorData.errors.map(e => e.msg || e.message).join(', ')
          : 'An error occurred');

      return {
        message: errorMessage,
        status: error.response.status,
        data: errorData,
      };
    }

    if (error.request) {
      return {
        message: 'Network error - please check your connection',
        status: 0,
      };
    }

    return {
      message: error.message || 'Unexpected error occurred',
      status: 0,
    };
  }
}

export default SourcingRfpService;